import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  Chip,
  CircularProgress,
  Tooltip,
  ListItemText
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { 
  AutoAwesome as AIIcon,
  AutoAwesomeRounded as SparkleIcon,
  TrendingUp as TrendIcon,
  SwapHoriz as SwapIcon,
  LocalOffer as DiscountIcon,
  AddBox as AddBoxIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es, it, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api.config';
import { DEFAULT_EXPERIMENT_WEEKS } from '../../constants/experiments';
import { getApiErrorMessage } from '../../utils/apiError';
import AppliedPricingDialog from '../shared/AppliedPricingDialog';

import { colors } from '../../themes/typography';
import AnimatedTypography from '../shared/AnimatedTypography';
import { renderWithAiHighlights } from './utils/highlight';
import { fetchAIRecommendation, calculateDiscountedPrice, validateDiscount } from './aiRecommendationService';

// Use centralized API base config (prod/dev/local handled in api.config)

/**
 * AI Recommendation View Component
 * Shows AI-generated experiment recommendations with elegant interface
 */
const AIRecommendationView = ({ 
  problem,
  storeId,
  productId,
  clientId,
  token,
  onClose, 
  onDataRefresh,
  t 
}) => {
  const { i18n } = useTranslation();
  const dateFnsLocale = { es, en: enUS, it }[i18n.language] || es;
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [error, setError] = useState(null);
  const [priceReview, setPriceReview] = useState(null);
  const [experimentId, setExperimentId] = useState(null);
  
  // Form state
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [discount, setDiscount] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [status, setStatus] = useState('manual_review');
  const [discountError, setDiscountError] = useState('');
  
  // Load AI recommendation
  useEffect(() => {
    const loadRecommendation = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await fetchAIRecommendation(
          problem.id,
          clientId,
          storeId,
          productId
        );
        
        // Normalize numeric identifiers to avoid string/number mismatches
        const normalizedCandidateIds = (data.candidateProductIds || []).map((id) =>
          typeof id === 'string' ? parseInt(id, 10) : id
        );
        
        const normalizedSelectedCandidate = data.selectedCandidate !== undefined && data.selectedCandidate !== null
          ? (typeof data.selectedCandidate === 'string' ? parseInt(data.selectedCandidate, 10) : data.selectedCandidate)
          : null;
        
        setRecommendation({ ...data, candidateProductIds: normalizedCandidateIds });
        setExperimentId(data.experimentId || null);
        setSelectedCandidate(normalizedSelectedCandidate);
        setDiscount(data.discount?.toString() || '0');

        // Prefer API-provided dates but always enforce an 8-week span
        const parsedFrom = data?.dateFrom ? new Date(data.dateFrom) : null;
        const parsedTo = data?.dateTo ? new Date(data.dateTo) : null;
        const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());

        const weeksToDays = (weeks) => weeks * 7;
        const addDays = (base, days) => {
          const d = new Date(base);
          d.setDate(d.getDate() + days);
          return d;
        };

        if (isValidDate(parsedFrom)) {
          const enforcedTo = addDays(parsedFrom, weeksToDays(DEFAULT_EXPERIMENT_WEEKS));
          setDateFrom(parsedFrom);
          setDateTo(enforcedTo);
        } else if (isValidDate(parsedTo)) {
          const enforcedFrom = addDays(parsedTo, -weeksToDays(DEFAULT_EXPERIMENT_WEEKS));
          setDateFrom(enforcedFrom);
          setDateTo(parsedTo);
        } else {
          const defaultStart = new Date();
          defaultStart.setDate(defaultStart.getDate() + 1); // Tomorrow
          const defaultEnd = addDays(defaultStart, weeksToDays(DEFAULT_EXPERIMENT_WEEKS));
          setDateFrom(defaultStart);
          setDateTo(defaultEnd);
        }
        setStatus('accepted'); // Default to accepted instead of manual_review
      } catch (err) {
        setError(t('errorLoadingAIRecommendation'));
        console.error('Error loading AI recommendation:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (problem && problem.id) {
      loadRecommendation();
    }
  }, [problem?.id, clientId, storeId, productId]);
  
  // Handle discount input change
  const handleDiscountChange = (value) => {
    setDiscount(value);
    
    if (value) {
      const validation = validateDiscount(value);
      setDiscountError(validation.isValid ? '' : validation.error);
    } else {
      setDiscountError('');
    }
  };
  
  // Calculate real-time discounted price
  const discountedPrice = recommendation?.originalPrice && discount
    ? calculateDiscountedPrice(recommendation.originalPrice, parseFloat(discount) || 0)
    : recommendation?.originalPrice || 0;
  
  // Handle form submission
  const handleAcceptRecommendation = async (pricingOverride = null) => {
    try {
      setSubmitting(true);
      
      // Prepare request body (same format as manual experiment creation)
      const requestBody = {
        experimentType: recommendation.experimentType,
        status: status,
        dateFrom: format(dateFrom, 'yyyy-MM-dd'),
        dateTo: format(dateTo, 'yyyy-MM-dd'),
        innovation: false
      };
      
      if (recommendation.experimentType === 'promo_precio_unitario') {
        requestBody.discount = parseFloat(discount);
      } else if (recommendation.experimentType === 'alza_precio_sugerido') {
        // Una subida de precio no cambia el producto: no lleva candidato ni descuento. El
        // `markup` ya está en la fila que escribió el orquestador y se respeta salvo que el
        // operador lo cambie, así que aceptar sin tocarlo no manda nada de precio.
      } else {
        requestBody.candidateProductId = selectedCandidate;
        requestBody.discount = 0;
      }
      
      // Apply AI override to existing experiment instead of creating a new one
      if (!experimentId) {
        throw new Error('No experiment id found for AI override');
      }
      const url = `${API_BASE_URL}/experiments/${experimentId}/ai-override?clientId=${clientId}`;
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // The preview is deliberately read-only. It is the operator's final
      // chance to review or independently override every tender before an ERP
      // call and the database status transition happen.
      if (status === 'accepted' && !pricingOverride) {
        const preview = await axios.post(
          `${API_BASE_URL}/experiments/pricing-preview?clientId=${clientId}`,
          { experimentId, ...requestBody },
          { headers },
        );
        if (preview.data?.pricing) {
          setPriceReview(preview.data.pricing);
          return;
        }
      }
      if (pricingOverride) requestBody.pricingOverride = pricingOverride;
      await axios.post(url, requestBody, { headers });

      // Success. No confirmation of what the ERP applied: the operator already saw and
      // approved those exact tenders in the preview above, so repeating them after the fact
      // only adds a click.
      if (onDataRefresh) {
        onDataRefresh();
      }
      onClose();
    } catch (error) {
      console.error('Error accepting AI recommendation:', error);
      setError(getApiErrorMessage(error, 'Error creando experimento'));
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        py: 4,
        gap: 2
      }}>
        <Box sx={{
          animation: 'pulse 2s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': { transform: 'scale(1)', opacity: 0.8 },
            '50%': { transform: 'scale(1.1)', opacity: 1 },
            '100%': { transform: 'scale(1)', opacity: 0.8 },
          },
        }}>
          <AIIcon sx={{ 
            fontSize: '40px',
            color: '#6B46C1'
          }} />
        </Box>
        <Typography sx={{
          fontSize: '14px',
          color: colors.text.secondary,
          textAlign: 'center'
        }}>
          {t('generatingAIRecommendation')}
        </Typography>
        <CircularProgress 
          size={20} 
          sx={{ color: '#6B46C1' }}
        />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        py: 4,
        gap: 2
      }}>
        <Typography sx={{
          fontSize: '14px',
          color: colors.error || '#f44336',
          textAlign: 'center'
        }}>
          {error}
        </Typography>
        <Button 
          onClick={() => window.location.reload()}
          sx={{ fontSize: '12px' }}
        >
          {t('tryAgain')}
        </Button>
      </Box>
    );
  }
  
  if (!recommendation) {
    return null;
  }
  
  const isLaneExpansion = recommendation.experimentType === 'ampliar_carril';
  const isReplacement = recommendation.experimentType === 'reemplazo_producto' || isLaneExpansion;
  const isDiscount = recommendation.experimentType === 'promo_precio_unitario';
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
      <AppliedPricingDialog
        open={Boolean(priceReview)}
        pricing={priceReview}
        onClose={() => setPriceReview(null)}
        onConfirm={(pricingOverride) => { setPriceReview(null); handleAcceptRecommendation(pricingOverride); }}
        loading={submitting}
      />
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 1,
        p: 1,
        maxHeight: '60vh',
        overflowY: 'auto'
      }}>
        {/* AI Recommendation */}
        <Box sx={{
          background: 'linear-gradient(135deg, rgba(107, 70, 193, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
          borderRadius: '6px',
          border: '1px solid rgba(107, 70, 193, 0.2)',
          p: 1,
          backdropFilter: 'blur(10px)'
        }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <AIIcon sx={{ fontSize: '14px', color: '#6B46C1' }} />
            <Typography sx={{
              fontSize: '12px',
              color: '#37352f',
              fontWeight: 700,
              lineHeight: 1.3
            }}>
              {t('aiAgent') || 'URGE AI'}
            </Typography>
          </Box>
          
          {/* AI Recommendation Text as bullets with highlights */}
          {(() => {
            const raw = recommendation.aiRecommendation?.[0] || '';
            const bullets = raw.split(/\n{2,}/).filter(Boolean);
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {bullets.map((para, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box
                      sx={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: '#6B46C1',
                        mt: '6px',
                        flexShrink: 0
                      }}
                    />
                    <AnimatedTypography
                      typewriterSpeed={5}
                      startDelay={idx * 800}
                      showCursor={false}
                      sx={{
                        fontSize: '12px',
                        color: '#37352f',
                        fontWeight: 400,
                        lineHeight: 1.4
                      }}
                    >
                      {renderWithAiHighlights(para)}
                    </AnimatedTypography>
                  </Box>
                ))}
              </Box>
            );
          })()}
          
          {/* Slot Guidance - Only show if exists */}
          {recommendation.slotGuidance && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: 0.5, 
              mt: 1.5,
              pt: 1.5,
              borderTop: '1px solid rgba(107, 70, 193, 0.1)'
            }}>
              <InfoIcon sx={{ 
                fontSize: '14px', 
                color: '#ff9800',
                mt: 0.1,
                flexShrink: 0
              }} />
              <Typography sx={{
                fontSize: '11px',
                color: '#37352f',
                fontWeight: 500,
                lineHeight: 1.4
              }}>
                {recommendation.slotGuidance}
              </Typography>
            </Box>
          )}
        </Box>
        
        {/* Product and Configuration Box with Badge */}
        <Box sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '6px',
          p: 1.5,
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}>
          {/* Badge at top left */}
          <Box sx={{ mb: 1 }}>
            <Chip
              icon={isLaneExpansion ? <AddBoxIcon sx={{ fontSize: '10px' }} /> : isReplacement ? <SwapIcon sx={{ fontSize: '10px' }} /> : <DiscountIcon sx={{ fontSize: '10px' }} />}
              label={isLaneExpansion ? t('laneExpansion') : isReplacement ? t('replacement') : t('discount')}
              sx={{
                // Ampliar carril lleva su paleta índigo de contrato (D7); el resto conserva el morado.
                backgroundColor: isLaneExpansion ? '#eef2ff' : '#f8f7ff',
                color: isLaneExpansion ? '#3730a3' : '#6B46C1',
                fontSize: '9px',
                fontWeight: 600,
                border: isLaneExpansion ? '1px solid #c7d2fe' : '1px solid #6B46C1',
                height: '18px',
                '& .MuiChip-icon': isLaneExpansion ? { color: '#3730a3' } : undefined
              }}
            />
          </Box>
          
          {/* Content Layout: Replacement on two lines, Discount inline */}
          {isReplacement ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Line 1: Current product label + name */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography sx={{
                  fontSize: '10px',
                  color: colors.text.secondary,
                  fontWeight: 500,
                }}>
                  {t('currentProduct')}
                </Typography>
                <Tooltip title={recommendation.productName} arrow placement="top">
                  <Typography sx={{
                    fontSize: '11px',
                    color: colors.text.primary,
                    fontWeight: 500,
                    minWidth: 0,
                    cursor: 'help',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {recommendation.productName}
                  </Typography>
                </Tooltip>
              </Box>
              {/* Line 2: Replacement label + dropdown */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography sx={{
                  fontSize: '10px',
                  color: colors.text.secondary,
                  fontWeight: 500,
                }}>
                  {t('replaceWith')}
                </Typography>
                <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                  <Select
                    value={selectedCandidate !== null ? selectedCandidate : ''}
                    onChange={(e) => {
                      if (e.target.value !== undefined && e.target.value !== '') {
                        setSelectedCandidate(typeof e.target.value === 'string' ? parseInt(e.target.value, 10) : e.target.value);
                      }
                    }}
                    displayEmpty
                    renderValue={(value) => {
                      const valNum = typeof value === 'string' ? parseInt(value, 10) : value;
                      const idx = recommendation.candidateProductIds.indexOf(valNum);
                      const name = idx >= 0 ? recommendation.candidateNames[idx] : '';
                      return (
                        <Tooltip title={name} arrow placement="top">
                          <Box sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%'
                          }}>
                            {name || 'Seleccionar candidato'}
                          </Box>
                        </Tooltip>
                      );
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxWidth: 440,
                          '& .MuiMenuItem-root': { maxWidth: 420 }
                        }
                      }
                    }}
                    sx={{
                      fontSize: '11px',
                      minHeight: '28px',
                      minWidth: '150px',
                      maxWidth: '100%',
                      '& .MuiSelect-select': {
                        py: 0.5,
                        fontSize: '11px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      },
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#6B46C1'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#6B46C1'
                        }
                      }
                    }}
                  >
                    {recommendation.candidateProductIds.map((id, index) => {
                      const name = recommendation.candidateNames[index];
                      return (
                        <MenuItem key={id} value={id} sx={{ fontSize: '11px', py: 0.5, maxWidth: 420 }}>
                          <ListItemText
                            primary={name}
                            primaryTypographyProps={{
                              noWrap: true,
                              sx: {
                                fontSize: '11px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '380px'
                              }
                            }}
                          />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
              {/* Product Name with hover tooltip */}
              <Tooltip title={recommendation.productName} arrow placement="top">
                <Typography sx={{
                  fontSize: '11px',
                  color: colors.text.primary,
                  fontWeight: 500,
                  flex: 1,
                  minWidth: 0,
                  cursor: 'help',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {recommendation.productName}
                </Typography>
              </Tooltip>
              {/* For Discount Experiments: Price Display and Input - Right aligned */}
              {isDiscount && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{
                    fontSize: '11px',
                    color: colors.text.secondary,
                    fontWeight: 500,
                    flexShrink: 0
                  }}>
                    €{recommendation.originalPrice?.toFixed(2)}
                  </Typography>
                  <Typography sx={{
                    fontSize: '11px',
                    color: colors.text.secondary,
                    fontWeight: 500,
                    flexShrink: 0
                  }}>
                    →
                  </Typography>
                  <Typography sx={{
                    fontSize: '11px',
                    color: colors.text.primary,
                    fontWeight: 600,
                    flexShrink: 0
                  }}>
                    €{discountedPrice.toFixed(2)}
                  </Typography>
                  <TextField
                    value={discount}
                    onChange={(e) => handleDiscountChange(e.target.value)}
                    size="small"
                    placeholder="15"
                    error={!!discountError}
                    inputProps={{ 
                      style: { 
                        fontSize: '11px',
                        textAlign: 'center',
                        width: '30px',
                        padding: '6px'
                      }
                    }}
                    sx={{
                      width: '42px',
                      flexShrink: 0,
                      '& .MuiOutlinedInput-root': {
                        height: '28px',
                        '&:hover fieldset': {
                          borderColor: '#6B46C1'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#6B46C1'
                        }
                      }
                    }}
                  />
                  <Typography sx={{
                    fontSize: '11px',
                    color: colors.text.secondary,
                    fontWeight: 500,
                    flexShrink: 0
                  }}>
                    %
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          
          {/* Error message below if needed */}
          {discountError && (
            <Typography sx={{
              fontSize: '10px',
              color: colors.error || '#f44336',
              mt: 0.5,
              textAlign: 'center'
            }}>
              {discountError}
            </Typography>
          )}
          
          {/* Divider */}
          <Box sx={{
            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
            my: 1.5
          }} />
          
          {/* Date Row - Two dates side by side */}
          <Box sx={{ display: 'flex', gap: 1.5, overflow: 'hidden' }}>
            {/* Start Date */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
              <Typography sx={{
                fontSize: '12px',
                color: colors.text.primary,
                fontWeight: 500
              }}>
                {t('startLabel')}:
              </Typography>
              <DatePicker
                value={dateFrom}
                onChange={(newDate) => {
                  if (!newDate || isNaN(newDate)) { setDateFrom(newDate); return; }
                  // Always enforce 8 weeks between start and end
                  const adjustedEnd = new Date(newDate);
                  adjustedEnd.setDate(adjustedEnd.getDate() + (DEFAULT_EXPERIMENT_WEEKS * 7));
                  setDateFrom(newDate);
                  setDateTo(adjustedEnd);
                }}
                slots={{ textField: TextField }}
                slotProps={{ 
                  textField: { 
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        height: '28px',
                        fontSize: '12px',
                        '& input': {
                          fontSize: '12px',
                          py: 0.5,
                          px: 0.75
                        },
                        '&:hover fieldset': {
                          borderColor: '#6B46C1'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#6B46C1'
                        }
                      },
                      '& .MuiInputAdornment-root': {
                        '& .MuiIconButton-root': {
                          padding: '4px',
                          '& .MuiSvgIcon-root': {
                            fontSize: '14px'
                          }
                        }
                      }
                    }
                  } 
                }}
                enableAccessibleFieldDOMStructure={false}
              />
            </Box>
            
            {/* End Date */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
              <Typography sx={{
                fontSize: '12px',
                color: colors.text.primary,
                fontWeight: 500
              }}>
                {t('endLabel')}:
              </Typography>
              <DatePicker
                value={dateTo}
                onChange={(newDate) => {
                  if (!newDate || isNaN(newDate)) { setDateTo(newDate); return; }
                  // Keep dates 8 weeks apart by adjusting start when end changes
                  const adjustedStart = new Date(newDate);
                  adjustedStart.setDate(adjustedStart.getDate() - (DEFAULT_EXPERIMENT_WEEKS * 7));
                  setDateFrom(adjustedStart);
                  setDateTo(newDate);
                }}
                slots={{ textField: TextField }}
                slotProps={{ 
                  textField: { 
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        height: '28px',
                        fontSize: '12px',
                        '& input': {
                          fontSize: '12px',
                          py: 0.5,
                          px: 0.75
                        },
                        '&:hover fieldset': {
                          borderColor: '#6B46C1'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#6B46C1'
                        }
                      },
                      '& .MuiInputAdornment-root': {
                        '& .MuiIconButton-root': {
                          padding: '4px',
                          '& .MuiSvgIcon-root': {
                            fontSize: '14px'
                          }
                        }
                      }
                    }
                  } 
                }}
                enableAccessibleFieldDOMStructure={false}
              />
            </Box>
          </Box>
          
          {/* Divider */}
          <Box sx={{
            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
            my: 1.5
          }} />
          
          {/* Status Row */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography sx={{
              fontSize: '12px',
              color: colors.text.primary,
              fontWeight: 500
            }}>
              {t('status')}:
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                sx={{
                  height: '26px',
                  fontSize: '12px',
                  '& .MuiSelect-select': {
                    py: 0.5,
                    px: 0.75,
                    fontSize: '12px'
                  },
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#6B46C1'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6B46C1'
                    }
                  }
                }}
              >
                <MenuItem value="accepted" sx={{ fontSize: '12px' }}>{t('accepted')}</MenuItem>
                <MenuItem value="pending" sx={{ fontSize: '12px' }}>{t('pending')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        
        {/* Accept Button */}
        <Button
          variant="contained"
          size="large"
          // Sin envolver, React pasa el evento del click como `pricingOverride`: al ser truthy
          // se saltaba la vista previa de precios y el evento acababa en el body, donde
          // JSON.stringify revienta con "cyclic object value".
          onClick={() => handleAcceptRecommendation()}
          disabled={submitting || !!discountError || !dateFrom || !dateTo}
          sx={{
            background: 'linear-gradient(135deg, #6B46C1 0%, #7C3AED 100%)',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            py: 1.5,
            borderRadius: '8px',
            textTransform: 'none',
            '&:hover': {
              background: 'linear-gradient(135deg, #5938a5 0%, #6d28d9 100%)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(107, 70, 193, 0.3)'
            },
            '&:active': {
              transform: 'translateY(0px)'
            },
            '&:disabled': {
              background: colors.text.disabled || '#cccccc',
              color: 'white'
            }
          }}
        >
          {submitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} sx={{ color: 'white' }} />
              <span>{t('creatingExperiment')}</span>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SparkleIcon sx={{ fontSize: '18px' }} />
              <span>{t('acceptAIRecommendation')}</span>
            </Box>
          )}
        </Button>
        
        {error && (
          <Typography sx={{
            fontSize: '12px',
            color: colors.error || '#f44336',
            textAlign: 'center',
            mt: 1
          }}>
            {error}
          </Typography>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default AIRecommendationView;
